<template>
  <v-app>
    <v-navigation-drawer
        v-model="drawer"
        app
        v-if="isLoggedIn"
    >
      <v-list dense>
        <v-list-item>
          <v-list-item-avatar>
            <v-avatar color="teal" size="36">
              <span class="white--text headline">{{ username ? username[0].toUpperCase() : '' }}</span>
            </v-avatar>
          </v-list-item-avatar>
          <v-list-item-content>
            {{ fullName }}
          </v-list-item-content>
        </v-list-item>
        <v-divider class="mb-2"></v-divider>
        <v-list-item v-if="isAdmin">
          <v-list-item-content>
            <v-alert
                border="top"
                color="red lighten-2"
                dark
                class="text-center mb-1"
            >
              Admin Mode
            </v-alert>
          </v-list-item-content>
        </v-list-item>
        <template v-for="(item, i) in items">
          <v-list-item
              :key="i" :to="item.route ? item.route : undefined"
              :href="item.href ? item.href : undefined"
              :target="item.target ? item.target : undefined"
          >
            <v-list-item-action>
              <v-icon>{{ item.icon }}</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>{{ item.title }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
      </v-list>
    </v-navigation-drawer>

    <v-app-bar
        app
        dark
        color="primary"
        v-if="isLoggedIn"
    >
      <v-app-bar-nav-icon @click.stop="drawer = !drawer"></v-app-bar-nav-icon>
      <v-toolbar-title></v-toolbar-title>
    </v-app-bar>
    <v-container fluid v-if="isLoggedIn">
      <router-view></router-view>
    </v-container>
    <v-container
        v-else
        fluid
        fill-height
    >
      <router-view/>
    </v-container>
    <v-snackbar
        :timeout="-1"
        :value="notificationsPermission !== 'granted'"
        color="orange darken-2"
    >
      <div class="d-flex flex-row">
        <v-icon
            left
            color="white"
        >
          mdi-bell
        </v-icon>
        <div v-if="notificationsPermission === 'denied'">
          Notification are blocked, please allow notifications manually and refresh the page for better application
          experience.
        </div>
        <div v-else>
          Looks like push notifications are not allowed, please "ALLOW" notifications for better application experience.
        </div>
      </div>
      <template v-if="notificationsPermission !== 'denied'" v-slot:action>
        <v-btn
            text
            color="white"
            @click="requestNotificationPermission"
        >
          Allow
        </v-btn>
      </template>
    </v-snackbar>
    <v-footer
        color="primary"
        app
    >
      <span class="d-inline-block font-weight-bold white--text">Version {{ appVersion }}</span>
      <span class="flex-grow-1"></span>
      <span class="d-inline-block white--text">&copy; VitalDevelopers.com, 2020</span>
    </v-footer>
    <snackbar></snackbar>
    <confirm ref="confirm"></confirm>
    <div v-if="notifications">
      <notify
          v-for="(notification, i) in notifications"
          :key="'notification_' + i"
          :notification="notification"
      />
    </div>
    <v-snackbar
        :value="!!overdueMessagesTotal && !['Dashboard', 'Home'].includes(currentRouteName)"
        :timeout="-1"
        top
        color="pink"
        class="font-weight-bold"
    >
      You have {{ overdueMessagesTotal }} unanswered {{ overdueMessagesTotal > 1 ? 'messages' : 'message' }} older than
      24
      hours
      <v-btn text to="/dashboard/">Check Dashboard</v-btn>
    </v-snackbar>
  </v-app>
</template>

<script>
import {mapGetters} from 'vuex'
import Snackbar from './components/App/Snackbar'
import Notify from './components/App/Notify'
import Confirm from './components/App/Confirm'
// import jwtDecode from 'jwt-decode'
import moment from 'moment'

export default {
  name: 'app',
  data() {
    return {
      drawer: true,
      items: [
        {
          title: 'Home',
          icon: 'mdi-monitor-dashboard',
          route: '/dashboard'
        },
        {
          title: 'Applications',
          icon: 'mdi-account-box-multiple',
          route: '/applications'
        },
        {
          title: 'Help',
          icon: 'mdi-help',
          route: '/help'
        },
        {
          title: 'Photo Guidelines',
          icon: 'mdi-camera',
          route: '/guidelines-photo'
        },
        {
          title: 'Processing Guidelines',
          icon: 'mdi-book',
          href: '/dl/daa892a8/processing_guidelines.pdf',
          target: '_blank'
        },
        {
          title: 'Log Out',
          icon: 'mdi-logout',
          route: '/logout'
        }
      ],
      socket: null,
      socketConnectAttempts: 0
    }
  },
  computed: {
    ...mapGetters('auth', [
      'isLoggedIn',
      'isAdmin',
      'username',
      'fullName'
    ]),
    ...mapGetters({
      notifications: 'notifications/queue',
      notificationsPermission: 'notifications/permission',
      appVersion: 'app/version'
    }),
    ...mapGetters({
      overdueMessagesTotal: 'messages/overdue',
    }),
    overdueMessagesAlert() {
      return !!this.overdueMessagesTotal
    },
    currentRouteName() {
      return this.$route.name
    }
  },
  watch: {
    isLoggedIn(v) {
      if (v) {
        this.createSocket()
        this.$root.$emit('fetch-counters')
      }
    }
  },
  methods: {
    createSocket() {
      if (this.socketConnectAttempts < 10 && this.isLoggedIn) {
        // TODO: Change it to Cookie based auth
        this.socket = new WebSocket(
            `${process.env.VUE_APP_WS_URL}/ws/notifications/?token=${this.$store.getters['auth/token']}`
        )
        this.socket.addEventListener('message', this.socketMessageListener);
        this.socket.addEventListener('close', this.createSocket);
      }
    },
    socketMessageListener(event) {
      const data = JSON.parse(event.data)
      if (data.type === 'push') {
        this.$store.dispatch('notifications/push', data.notification)
      }
    },
    fetchCounters() {
      this.$axios.get(`/dashboard/messages/unanswered/overdue/total`, {})
          .then((response) => {
            let total = response.data.total
            this.$store.dispatch('messages/overdue', total)
          })
          .catch((e) => {
            this.$store.dispatch('snackbar/error', e.response.status + ' ' + e.response.statusText)
          })
    },
    checkAuthExpiration() {
      if (!this.$store.getters['auth/isLoggedIn']) {
        return
      }
      let sessionExp = moment(this.$store.getters['auth/refreshExp'] * 1000)
      let secondsLeft = sessionExp.diff(moment(), 'seconds')
      // console.log(
      //     `Auth Token Exp: ${moment(this.$store.getters['auth/accessExp'] * 1000).fromNow()}`,
      //     `Refresh Token Exp: ${moment(this.$store.getters['auth/refreshExp'] * 1000).fromNow()}`,
      //     `Difference: ${secondsLeft}`
      // )
      if (secondsLeft < 900) {
        this.$store.dispatch(
            'snackbar/warning',
            `Your authentication session will expire ${sessionExp.fromNow()}, please save your data.`
        )
      }
      if (this.$store.getters['auth/isSessionExpired']()) {
        this.$store.dispatch('auth/logout')
      }
    },
    requestNotificationPermission() {
      if (Notification.permission === 'granted') {
        this.$store.dispatch('notifications/permissionGranted')
      } else if (Notification.permission !== 'denied') {
        Notification.requestPermission((permission) => {
          if (permission === 'granted') {
            this.$store.dispatch('notifications/permissionGranted')
          } else {
            this.$store.dispatch('notifications/permissionDenied')
          }
        })
      }
    }
  },
  created() {
    // this.$root.$on('fetch-counters', this.fetchCounters())
  },
  mounted() {
    this.$root.$confirm = this.$refs.confirm.open
    // Event busing :)
    this.$root.$on('fetch-counters', this.fetchCounters)
    // Desktop Notifications permissions
    this.createSocket()
    if (this.isLoggedIn) {
      this.$root.$emit('fetch-counters')
    }
    window.setInterval(() => {
      this.checkAuthExpiration()
    }, 10000)
    // window.setInterval(() => {
    //     let token = jwtDecode(this.$store.getters['auth/token'])
    // }, 5000)
  },
  components: {
    Confirm,
    Notify,
    Snackbar
  }
}
</script>
